// SAB-427 / SAB-575: above-the-fold hero.
//
// SAB-575 changes: the hero CTAs now sell intent - Create a League (primary,
// the single orange slot) + Browse Leagues + How it works - with an eligibility
// line beneath. On DESKTOP the right column hosts the inline signup card
// (InlineSignupCard). Headline, status pill, subhead and the three green checks
// are unchanged.
//
// SAB-578: the "Trash Scoring: Live" ScoreboardPanel no longer renders in the hero
// on any breakpoint; it moved into HowItWorks beside the Week 1 example line.
import { Link } from 'react-router-dom';
import { Check, ArrowRight } from 'lucide-react';
import { trackEvent } from '@/lib/analytics';
import { InlineSignupCard } from '@/components/auth/InlineSignupCard';
import { HERO_ELIGIBILITY_LINE, CREATE_LEAGUE_INTENT } from '@/lib/landingAuthCopy';

const OUTLINE_CTA =
  'inline-flex items-center justify-center rounded-xl h-[50px] sm:h-14 font-display font-bold text-[15px] sm:text-base text-foreground transition-colors';
const OUTLINE_STYLE = { background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.16)' } as const;

export function Hero() {
  const trackCreate = () =>
    trackEvent('homepage_create_league_click', { source: 'homepage', placement: 'hero' });
  const trackBrowse = () =>
    trackEvent('homepage_browse_leagues_click', { source: 'homepage', placement: 'hero' });
  const trackHowItWorks = () =>
    trackEvent('homepage_how_it_works_click', { source: 'homepage', placement: 'hero' });

  return (
    <section id="top" className="relative overflow-hidden border-b border-border">
      {/* Blurred radial blobs */}
      <div aria-hidden className="pointer-events-none absolute rounded-full"
        style={{ top: -160, right: -120, width: 520, height: 520, background: 'hsl(25 95% 53% / 0.22)', filter: 'blur(120px)' }} />
      <div aria-hidden className="pointer-events-none absolute rounded-full"
        style={{ bottom: -200, left: -140, width: 520, height: 520, background: 'hsl(0 84% 60% / 0.16)', filter: 'blur(120px)' }} />

      <div className="relative mx-auto max-w-[1180px] px-6 pt-9 pb-10 sm:pt-[52px] sm:pb-14">
        <div className="grid items-center gap-12 max-[900px]:gap-8 grid-cols-[1fr_420px] max-[900px]:grid-cols-1">
          {/* Left column */}
          <div>
            <div className="inline-flex items-center gap-2 mb-4 sm:mb-6 px-3.5 py-1.5 rounded-full border border-border"
              style={{ background: 'hsl(222 47% 8%)' }}>
              <span className="w-2 h-2 rounded-full bg-[hsl(var(--success))]" style={{ animation: 'sf-hero-pulse 2s infinite' }} />
              <span className="font-display text-[11px] uppercase tracking-[0.06em] font-bold text-foreground">
                NFL leagues open July. Season kicks off Sept
              </span>
            </div>

            <h1 className="font-varsity m-0 mb-3 sm:mb-5 leading-[1.05]" style={{ fontSize: 'clamp(2rem, 6.4vw, 4.6rem)' }}>
              Real fantasy.<br />
              Real money.<br />
              Rooting for <span className="text-gradient-damage">chaos.</span>
            </h1>

            <p className="text-[16px] sm:text-[19px] text-muted-foreground max-w-[520px] mb-5 sm:mb-[30px] leading-[1.55]">
              Scoring is <b className="text-foreground">inverted</b>. Interceptions, sacks, fumbles
              and missed kicks score the points, so you draft for the chaos, not the highlight
              reel. Read the game differently and the trash adds up.
            </p>

            {/* CTAs — mobile: Create (full) then [Browse | How it works] row.
                desktop: one row (sm:contents lifts the two outlines into the row). */}
            <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3 mb-3.5">
              <Link
                to={`/signup?intent=${CREATE_LEAGUE_INTENT}`}
                onClick={trackCreate}
                className="inline-flex items-center justify-center gap-2 w-full sm:w-auto sm:px-7 h-14 rounded-xl font-display font-bold text-[17px] text-primary-foreground bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary)/0.9)] transition-colors"
              >
                Create a League <ArrowRight className="h-5 w-5" />
              </Link>
              <div className="grid grid-cols-2 gap-3 sm:contents">
                <Link to="/nfl/leagues/browse" onClick={trackBrowse} className={`${OUTLINE_CTA} sm:px-6`} style={OUTLINE_STYLE}>
                  Browse Leagues
                </Link>
                <a href="#how" onClick={trackHowItWorks} className={`${OUTLINE_CTA} sm:px-6`} style={OUTLINE_STYLE}>
                  How it works
                </a>
              </div>
            </div>

            {/* Eligibility line — COPY: pending legal (SAB-575), via landingAuthCopy */}
            <p className="text-[12.5px] sm:text-[13px] mb-6 sm:mb-8" style={{ color: 'rgba(255,255,255,0.42)' }}>
              {HERO_ELIGIBILITY_LINE}
            </p>

            {/* Value pillars (unchanged) */}
            <div className="flex flex-wrap items-center gap-x-5 gap-y-2.5">
              <div className="flex items-center gap-2">
                <Check className="w-4 h-4 text-accent" strokeWidth={3} />
                <span className="text-sm text-muted-foreground">Free or paid leagues</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="w-4 h-4 text-accent" strokeWidth={3} />
                <span className="text-sm text-muted-foreground">Legal in eligible states</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="w-4 h-4 text-accent" strokeWidth={3} />
                <span className="text-sm text-muted-foreground">Season-long, not DFS</span>
              </div>
            </div>
          </div>

          {/* Right column — desktop: inline signup card. */}
          <div className="max-[900px]:hidden">
            <InlineSignupCard />
          </div>

          {/* SAB-578: the Trash Scoring card no longer lives in the hero — it moved
              into HowItWorks beside the Week 1 example line, on both breakpoints. */}

          {/* SAB-447: mobile-only inverted-scoring hook (unchanged). */}
          <div className="sm:hidden">
            <h2 className="m-0 mb-3 font-display font-extrabold" style={{ fontSize: 'clamp(1.7rem, 6vw, 2.2rem)' }}>
              Wait, the bad plays <span className="text-gradient-damage">score?</span>
            </h2>
            <p className="text-[15px] text-muted-foreground leading-[1.5]">
              Exactly. Touchdowns are boring. The chaos is what pays. Turnovers, sacks,
              fumbles, missed kicks and penalties all rack up points.
            </p>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes sf-hero-pulse {
          0%, 100% { box-shadow: 0 0 0 0 hsl(160 84% 39% / 0.5); }
          50%      { box-shadow: 0 0 0 6px hsl(160 84% 39% / 0); }
        }
      `}</style>
    </section>
  );
}

export default Hero;
